import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * AdminDemoRequestsPage — V22.9.9
 *
 * Route:  /admin/demo-requests
 * Access: admin / bcm_manager
 *
 * Pending queue with full requester details + one-click "Allow DEMO Access".
 * Approval response includes a manual-mode email body the admin can copy
 * (SendGrid integration is gated on the user's API key).
 */
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import { Mail, Copy, CheckCircle2, XCircle, ShieldCheck, RefreshCw, Loader2, Ban, Undo2, ShieldAlert, Clock, KeyRound, Sparkles, TimerReset } from "lucide-react";
import { toast } from "sonner";
import AuditDigestPanel from "../components/admin/AuditDigestPanel";

const STATUS_LABEL = { pending: "Pending", approved: "Approved", deferred: "Deferred", suppressions: "Suppressions", digest: "Digest" };

export default function AdminDemoRequestsPage() {
  const { api } = useAuth();
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, deferred: 0 });
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [emailModal, setEmailModal] = useState(null);  // {body, subject, credentials}
  const [search, setSearch] = useState("");
  const [suppressions, setSuppressions] = useState([]);
  const [supCounts, setSupCounts] = useState({ total: 0, by_reason: {} });
  // V23.0.2 — Approval dialog state. Admin can supply a custom initial
  // password and/or override the 48-hour default access window.
  const [approveDialog, setApproveDialog] = useState(null);  // {req} when open
  const [approveDuration, setApproveDuration] = useState(48);
  const [approveCustomPw, setApproveCustomPw] = useState("");
  // V23.0.3 — Extend dialog
  const [extendDialog, setExtendDialog] = useState(null);  // {req} when open
  const [extendHours, setExtendHours] = useState(48);
  const [extendNotify, setExtendNotify] = useState(true);
  const [extendBusy, setExtendBusy] = useState(false);

  const loadSuppressions = async () => {
    try {
      const res = await api.get("/email/suppressions");
      setSuppressions(res.data?.suppressions || []);
      setSupCounts(res.data?.counts || { total: 0, by_reason: {} });
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to load suppressions"));
    }
  };

  const removeSuppression = async (email) => {
    try {
      await api.delete(`/email/suppressions/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email} from suppression list`);
      await loadSuppressions();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to remove"));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/demo-requests", { params: tab !== "all" ? { status: tab } : {} });
      setRequests(res.data?.requests || []);
      setCounts(res.data?.counts || {});
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to load demo requests"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "suppressions") loadSuppressions();
    else load();
    /* eslint-disable-next-line */
  }, [tab]);

  const openApproveDialog = (req) => {
    setApproveDuration(48);
    setApproveCustomPw("");
    setApproveDialog({ req });
  };

  const openExtendDialog = (req) => {
    setExtendHours(48);
    setExtendNotify(true);
    setExtendDialog({ req });
  };

  const extend = async () => {
    const rid = extendDialog?.req?.id;
    if (!rid) return;
    const hours = parseInt(extendHours, 10);
    if (!hours || hours < 1 || hours > 168) {
      toast.error("Hours must be between 1 and 168");
      return;
    }
    setExtendBusy(true);
    try {
      const res = await api.post(`/demo-requests/${rid}/extend`, {
        additional_hours: hours,
        notify_user: extendNotify,
      });
      const data = res.data || {};
      const expires = data.new_expires_at ? new Date(data.new_expires_at).toLocaleString() : "—";
      if (data.email_sent) {
        toast.success(`+${hours}h granted · email sent · new expiry ${expires}`);
      } else if (extendNotify && data.email_error) {
        toast.warning(`+${hours}h granted but email failed (${data.email_error})`);
      } else {
        toast.success(`+${hours}h granted · new expiry ${expires}`);
      }
      setExtendDialog(null);
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Extension failed"));
    } finally {
      setExtendBusy(false);
    }
  };

  const approve = async () => {
    const rid = approveDialog?.req?.id;
    if (!rid) return;
    setActionBusy(rid);
    try {
      const body = {};
      if (approveDuration && approveDuration !== 48) body.duration_hours = parseInt(approveDuration, 10);
      if (approveCustomPw && approveCustomPw.trim()) body.initial_password = approveCustomPw.trim();
      const res = await api.post(`/demo-requests/${rid}/approve`, Object.keys(body).length ? body : undefined);
      const sent = res.data?.email_sent === true;
      setApproveDialog(null);
      setEmailModal({
        subject: res.data?.email_subject,
        body: res.data?.email_body,
        credentials: res.data?.credentials,
        emailSent: sent,
        emailProvider: res.data?.email_provider,
        messageId: res.data?.email_message_id,
        recipient: res.data?.credentials?.email,
      });
      if (sent) {
        toast.success(`✓ Welcome email sent to ${res.data?.credentials?.email}`);
      } else {
        toast.success(`Demo access provisioned · password copied below`);
        if (res.data?.email_error) {
          toast.warning(`SendGrid failed (${res.data.email_error}) — copy email body manually`);
        }
      }
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Approval failed"));
    } finally { setActionBusy(null); }
  };

  const reject = async (rid) => {
    if (!window.confirm("Defer this request? The submitter will silently re-queue on resubmit.")) return;
    setActionBusy(rid);
    try {
      await api.post(`/demo-requests/${rid}/reject`, {});
      toast.success("Request deferred");
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to defer"));
    } finally { setActionBusy(null); }
  };

  const filtered = requests.filter((r) =>
    !search
    || (r.requester_name || "").toLowerCase().includes(search.toLowerCase())
    || (r.organization_name || "").toLowerCase().includes(search.toLowerCase())
    || (r.business_email || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="admin-demo-requests-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <ShieldCheck className="w-5 h-5 text-violet-600" />
            Demo Requests · Admin
          </h1>
          <p className="text-sm text-slate-500">
            Public-to-Private Conversion Protocol · V22.9.9. One-click provisioning of demo accounts in the Meridian Demo Sandbox tenant.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search name / org / email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-60 h-9 text-xs"
            data-testid="admin-demo-requests-search"
          />
          <Button type="button" variant="outline" onClick={load} disabled={loading} data-testid="admin-demo-requests-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="admin-demo-tab-pending">
            Pending <Badge variant="outline" className="ml-1.5 text-[10px]">{counts.pending || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="admin-demo-tab-approved">
            Approved <Badge variant="outline" className="ml-1.5 text-[10px]">{counts.approved || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="deferred" data-testid="admin-demo-tab-deferred">
            Deferred <Badge variant="outline" className="ml-1.5 text-[10px]">{counts.deferred || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="suppressions" data-testid="admin-demo-tab-suppressions">
            <Ban className="w-3 h-3 mr-1" />
            Suppressions <Badge variant="outline" className="ml-1.5 text-[10px]">{supCounts.total || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="digest" data-testid="admin-demo-tab-digest">
            <ShieldAlert className="w-3 h-3 mr-1" />
            Digest
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "digest" ? (
        <AuditDigestPanel api={api} />
      ) : tab === "suppressions" ? (
        <Card data-testid="suppressions-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Ban className="w-4 h-4 text-rose-600" />
                  Email Suppression List
                </CardTitle>
                <p className="text-xs text-slate-400 mt-1">
                  RFC 8058 one-click unsubscribes, bounces &amp; spam reports — these addresses won't receive future emails.
                  {supCounts.by_reason && Object.keys(supCounts.by_reason).length > 0 && (
                    <span className="ml-2">
                      {Object.entries(supCounts.by_reason).map(([k, v]) => (
                        <Badge key={k} variant="outline" className="ml-1 text-[10px] capitalize">{k.replace("_", " ")}: {v}</Badge>
                      ))}
                    </span>
                  )}
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={loadSuppressions} data-testid="suppressions-refresh">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {suppressions.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">No suppressions yet — clean sender reputation ✓</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Added</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppressions.map((s) => (
                      <TableRow key={`${s.email}-${s.scope}`} data-testid={`suppression-row-${s.email}`}>
                        <TableCell className="font-mono text-[12px]">{s.email}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize text-[10px]">{(s.reason || "").replace("_", " ")}</Badge></TableCell>
                        <TableCell className="text-[11px] text-slate-500">{s.scope || "*"}</TableCell>
                        <TableCell className="text-[11px] text-slate-500">{(s.source || "").replace("_", " ")}</TableCell>
                        <TableCell className="text-[11px] text-slate-500">
                          {s.added_at ? new Date(s.added_at).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => removeSuppression(s.email)}
                            data-testid={`suppression-remove-${s.email}`}
                            className="text-[11px] h-7"
                          >
                            <Undo2 className="w-3 h-3 mr-1" /> Resubscribe
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">{STATUS_LABEL[tab] || "All"} Demo Requests</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-10 text-slate-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No {tab} requests.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requester</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Business Email</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Resilience Needs</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id} data-testid={`demo-request-row-${r.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{r.requester_name}</p>
                          <p className="text-[11px] text-slate-500">{r.job_title}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{r.organization_name}</TableCell>
                      <TableCell className="font-mono text-[11px]">{r.business_email}</TableCell>
                      <TableCell className="text-[11px] text-slate-500">
                        {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="text-[11px] max-w-xs truncate" title={r.resilience_needs}>
                        {r.resilience_needs}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.status === "pending" ? (
                          <div className="flex items-center gap-1.5 justify-end">
                            <Button
                              size="sm"
                              onClick={() => openApproveDialog(r)}
                              disabled={actionBusy === r.id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-[11px]"
                              data-testid={`demo-request-${r.id}-approve`}
                            >
                              {actionBusy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-0.5" />}
                              Grant DEMO Access
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => reject(r.id)}
                              disabled={actionBusy === r.id}
                              className="h-7 text-[11px] border-rose-200 text-rose-600 hover:bg-rose-50"
                              data-testid={`demo-request-${r.id}-defer`}
                            >
                              <XCircle className="w-3 h-3 mr-0.5" />
                              Defer
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1.5">
                            <Badge
                              variant="outline"
                              className={`text-[10px] capitalize ${r.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}
                              data-testid={`demo-request-${r.id}-status`}
                            >
                              {r.status}
                            </Badge>
                            {r.status === "approved" && r.provisioned_user_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openExtendDialog(r)}
                                className="h-7 text-[11px] border-sky-200 text-sky-700 hover:bg-sky-50"
                                data-testid={`demo-request-${r.id}-extend`}
                                title={r.demo_expires_at ? `Current expiry: ${new Date(r.demo_expires_at).toLocaleString()}` : "Extend access window"}
                              >
                                <TimerReset className="w-3 h-3 mr-0.5" />
                                Extend
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* V23.0.3 — Extend access window dialog */}
      <Dialog open={!!extendDialog} onOpenChange={(o) => !o && setExtendDialog(null)}>
        <DialogContent className="max-w-md" data-testid="extend-dialog" aria-describedby="extend-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <TimerReset className="w-4 h-4 text-sky-600" />
              Extend demo access
            </DialogTitle>
            <DialogDescription id="extend-desc" className="text-[11px] text-slate-500">
              Add more time to this approved demo user's window. Pushes both the JWT expiry and the user document forward.
            </DialogDescription>
          </DialogHeader>
          {extendDialog?.req && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-slate-50 px-3 py-2 text-xs space-y-0.5">
                <div><span className="text-slate-500">User:</span> <span className="font-mono">{extendDialog.req.business_email}</span></div>
                <div>
                  <span className="text-slate-500">Current expiry:</span>{" "}
                  {extendDialog.req.demo_expires_at
                    ? new Date(extendDialog.req.demo_expires_at).toLocaleString()
                    : "—"}
                </div>
                {(extendDialog.req.extensions || []).length > 0 && (
                  <div><span className="text-slate-500">Prior extensions:</span> {extendDialog.req.extensions.length}</div>
                )}
              </div>

              <div>
                <Label className="text-xs font-semibold flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-slate-500" />
                  Add hours
                </Label>
                <div className="flex items-center gap-2 mt-1.5">
                  {[24, 48, 72].map((h) => (
                    <Button
                      key={h}
                      type="button"
                      variant={parseInt(extendHours, 10) === h ? "default" : "outline"}
                      size="sm"
                      onClick={() => setExtendHours(h)}
                      data-testid={`extend-preset-${h}`}
                      className={`h-8 text-xs ${parseInt(extendHours, 10) === h ? "bg-sky-600 hover:bg-sky-700 text-white" : ""}`}
                    >
                      +{h}h
                    </Button>
                  ))}
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    value={extendHours}
                    onChange={(e) => setExtendHours(e.target.value)}
                    className="h-8 w-24 text-sm"
                    data-testid="extend-hours-input"
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Range 1-168 hours (= 7 days max).</p>
              </div>

              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="extend-notify-row">
                <input
                  type="checkbox"
                  checked={extendNotify}
                  onChange={(e) => setExtendNotify(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="extend-notify-checkbox"
                />
                <span>Send the user an email letting them know about the extension</span>
              </label>

              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setExtendDialog(null)}
                  data-testid="extend-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={extend}
                  disabled={extendBusy}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="extend-confirm-btn"
                >
                  {extendBusy
                    ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    : <TimerReset className="w-3.5 h-3.5 mr-1" />}
                  Extend by {extendHours}h
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* V23.0.2 — Approve confirmation dialog with optional custom password & duration */}
      <Dialog open={!!approveDialog} onOpenChange={(o) => !o && setApproveDialog(null)}>
        <DialogContent className="max-w-md" data-testid="approve-dialog" aria-describedby="approve-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              Grant demo access
            </DialogTitle>
            <DialogDescription id="approve-desc" className="text-[11px] text-slate-500">
              Provision a 48-hour observer account in the Meridian Demo Sandbox. Override the defaults below if needed.
            </DialogDescription>
          </DialogHeader>
          {approveDialog?.req && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-slate-50 px-3 py-2 text-xs space-y-0.5">
                <div><span className="text-slate-500">Name:</span> <strong>{approveDialog.req.requester_name}</strong></div>
                <div><span className="text-slate-500">Email:</span> <span className="font-mono">{approveDialog.req.business_email}</span></div>
                <div><span className="text-slate-500">Org:</span> {approveDialog.req.organization_name}</div>
              </div>

              <div>
                <Label htmlFor="approve-duration" className="text-xs font-semibold flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-slate-500" />
                  Access window (hours)
                </Label>
                <Input
                  id="approve-duration"
                  type="number"
                  min={1}
                  max={168}
                  value={approveDuration}
                  onChange={(e) => setApproveDuration(e.target.value)}
                  className="mt-1 h-9 text-sm"
                  data-testid="approve-duration-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">Default 48 hours. Min 1, max 168 (= 7 days).</p>
              </div>

              <div>
                <Label htmlFor="approve-custom-pw" className="text-xs font-semibold flex items-center gap-1">
                  <KeyRound className="w-3.5 h-3.5 text-slate-500" />
                  Initial password (optional)
                </Label>
                <Input
                  id="approve-custom-pw"
                  type="text"
                  value={approveCustomPw}
                  onChange={(e) => setApproveCustomPw(e.target.value)}
                  placeholder="Leave blank to auto-generate a 12-char password"
                  className="mt-1 h-9 text-sm font-mono"
                  data-testid="approve-custom-pw-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">If you set one, it must be at least 8 characters. Shared in the welcome email.</p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setApproveDialog(null)}
                  data-testid="approve-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={approve}
                  disabled={actionBusy === approveDialog?.req?.id}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="approve-confirm-btn"
                >
                  {actionBusy === approveDialog?.req?.id
                    ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  Provision & email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email-body modal — shows live-send confirmation or manual-mode fallback */}
      <Dialog open={!!emailModal} onOpenChange={(o) => !o && setEmailModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="demo-request-email-modal" aria-describedby="demo-email-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-violet-600" />
              {emailModal?.emailSent ? "Welcome email delivered" : "Welcome email — copy & send manually"}
            </DialogTitle>
            <DialogDescription id="demo-email-desc" className="text-[11px] text-slate-500">
              Demo credentials and welcome-email payload for the approved request.
            </DialogDescription>
          </DialogHeader>
          {emailModal && (
            <div className="space-y-3">
              {emailModal.emailSent ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[12px] text-emerald-800 flex items-start gap-2" data-testid="demo-request-email-sent">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mt-1.5"></span>
                  <div>
                    <strong>Email sent via SendGrid</strong> to <span className="font-mono">{emailModal.recipient}</span>.
                    {emailModal.messageId && <div className="mt-0.5 text-[10px] text-emerald-700 font-mono">Message ID: {emailModal.messageId}</div>}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800">
                  {emailModal.emailProvider === "sendgrid"
                    ? "SendGrid send failed — copy this email body and send via your normal mail client."
                    : "SendGrid isn't configured. Copy this email body and send via your normal mail client."}
                </div>
              )}
              <div className="space-y-1.5">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Demo Credentials (for your records)</p>
                <div className="font-mono text-[12px] bg-slate-50 border rounded px-2 py-2 space-y-0.5">
                  <div><span className="text-slate-500">Email:</span> {emailModal.credentials?.email}</div>
                  <div><span className="text-slate-500">Password:</span> {emailModal.credentials?.password}</div>
                  <div><span className="text-slate-500">Access window:</span> {emailModal.credentials?.duration_hours ?? 48}h{emailModal.credentials?.custom_password ? " · custom password" : ""}</div>
                  <div><span className="text-slate-500">Expires:</span> {emailModal.credentials?.expires_at ? new Date(emailModal.credentials.expires_at).toLocaleString() : "—"}</div>
                </div>
              </div>
              {!emailModal.emailSent && (
                <>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Subject</p>
                    <p className="text-sm bg-slate-50 border rounded px-2 py-1.5">{emailModal.subject}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Body</p>
                    <pre className="text-[11.5px] bg-slate-50 border rounded p-3 whitespace-pre-wrap font-mono leading-snug max-h-72 overflow-auto" data-testid="demo-request-email-body">
                      {emailModal.body}
                    </pre>
                  </div>
                </>
              )}
              <div className="flex gap-2">
                {!emailModal.emailSent && (
                  <Button
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(emailModal.body || "");
                      toast.success("Email body copied to clipboard");
                    }}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="demo-request-email-copy-body"
                  >
                    <Copy className="w-3 h-3 mr-1.5" />Copy email body
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setEmailModal(null)} data-testid="demo-request-email-close">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
